import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import {
  BarChart, Bar, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend, LabelList,
} from 'recharts';

import Button from '../../../components/ui/Button';
import PanelCard from '../../performance-operations/components/PanelCard';
import {
  Clickable, Sub, Table, TabButton, Tabs,
} from '../../performance-operations/styles';
import { usePanel } from '../../performance-operations/config';
import {
  bucketLabel, distributionRows, fetchDistribution, formatNum, formatPct, formatScore, metaOf, useBandColors,
} from '../config';

// PM analitik P2 — sonuç dağılımı paneli (E01 "Performans Dağılımı" + "Hedef Gerçekleşmesi Bantları", E03 "Puan Bandı").
// Sıralı sütun; her çubukta sayı ve yüzde; karşılaştırma dönemi ikinci seri (yalnız aynı ölçekte). Çubuk değeri yüzdedir —
// farklı n'li dönemler kıyaslanabilir. Final yoksa sıfır çubuk değil boş durum (İK'ya "Ön Sonuçları Göster"); küçük grupta
// hiçbir kova gösterilmez. Grafik seçimi aynı süzgeçli listeye iner (onSelect); tablo görünümünde de klavyeyle.
const KIND_FIELDS = {
  levels: { n: 'n', mean: 'mean', suppressed: 'suppressionReason' },
  bands: { n: 'bandN', mean: 'mean', suppressed: 'suppressionReason' },
  goalBands: { n: 'goalN', mean: 'goalMean', suppressed: 'goalSuppressionReason' },
};

export default function DistributionPanel({
  filters, panelKey, title, span, kinds = ['levels'], prelim = false, comparePeriod, onSelect, canShowPreliminary, onShowPreliminary, onMeta, onData,
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const theme = useTheme();
  const colors = useBandColors();
  const [kind, setKind] = useState(kinds[0]);
  const [table, setTable] = useState(false);
  const periodId = filters.periodId;
  const key = JSON.stringify(filters);
  const current = usePanel(() => fetchDistribution(filters, periodId, { prelim }), [key, prelim], !!periodId);
  // Ön sonuç kıyaslanmaz (karşılaştırma dönemi final halinde okunur; ön ile final aynı grafikte birleşmez).
  const withCompare = !!comparePeriod && !prelim;
  const compare = usePanel(() => fetchDistribution(filters, comparePeriod.id), [key, comparePeriod?.id], !!periodId && withCompare);

  useEffect(() => { if (current.data) onMeta?.(panelKey, metaOf(current.data)); }, [current.data, onMeta, panelKey]);
  useEffect(() => { onData?.(current.data || null); }, [current.data, onData]);
  // Sekme listesi değişirse (ör. ön sonuçta bant yok) geçersiz sekmeden ilk sekmeye dönülür.
  const kindsKey = kinds.join(',');
  useEffect(() => { if (!kinds.includes(kind)) setKind(kinds[0]); }, [kindsKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const data = current.data;
  const fields = KIND_FIELDS[kind];
  const { rows, comparable } = useMemo(
    () => distributionRows(data, withCompare && !compare.error ? compare.data : null, kind),
    [data, compare.data, compare.error, withCompare, kind],
  );
  const chartRows = rows.map((r) => ({
    ...r,
    name: bucketLabel(t, r.bucket, kind, lang),
    value: r.pct === null || r.pct === undefined ? 0 : Number(r.pct),
    compareValue: r.comparePct === null || r.comparePct === undefined ? null : Number(r.comparePct),
    label: r.count === null || r.count === undefined ? '' : `${formatNum(r.count, lang, 0)} · ${formatPct(r.pct, lang)}`,
  }));
  const n = data ? data[fields.n] : null;
  const suppressed = !!data?.[fields.suppressed];
  const disabled = kind === 'goalBands' && data && !data.goalEnabled;
  const empty = !suppressed && !disabled && (n === 0 || n === null || n === undefined);
  const showCompare = withCompare && comparable && rows.some((r) => r.compareCount !== null && r.compareCount !== undefined);
  const scaleMax = kind === 'goalBands' ? 100 : data?.scaleMax;

  let emptyText = null;
  if (disabled) emptyText = t('performance.results.analytics.dist.goalDisabled');
  else if (empty) {
    emptyText = (
      <>
        <span data-empty="no-final">{prelim ? t('performance.results.analytics.dist.noPreliminary') : t('performance.results.analytics.dist.noFinal')}</span>
        {!prelim && canShowPreliminary && onShowPreliminary ? (
          <Button variant="outline" onClick={onShowPreliminary} data-action="show-preliminary">{t('performance.results.analytics.showPreliminary')}</Button>
        ) : null}
      </>
    );
  }

  const select = (row) => { if (onSelect && row.count) onSelect(kind, row.bucket); };
  const compareName = comparePeriod?.name || '';

  return (
    <PanelCard
      panelKey={panelKey} title={title} span={span}
      loading={current.loading} error={current.error} onRetry={current.retry}
      empty={disabled || empty} emptyText={emptyText}
      tableToggle={!suppressed} showTable={table} onToggleTable={() => setTable((v) => !v)}
    >
      {kinds.length > 1 ? (
        <Tabs role="tablist">
          {kinds.map((k) => (
            <TabButton key={k} role="tab" aria-selected={kind === k} $active={kind === k} onClick={() => setKind(k)} data-tab={k}>
              {t(`performance.results.analytics.dist.kind.${k}`)}
            </TabButton>
          ))}
        </Tabs>
      ) : null}
      {suppressed ? (
        <Sub data-state="suppressed">{t('performance.results.analytics.dist.suppressed', { k: data.threshold })}</Sub>
      ) : (
        <>
          <Sub data-summary="1">
            {kind === 'bands'
              ? t('performance.results.analytics.dist.bandSummary', { n: formatNum(n, lang, 0) })
              : t('performance.results.analytics.dist.summary', { mean: formatScore(data?.[fields.mean], scaleMax, lang), n: formatNum(n, lang, 0) })}
          </Sub>
          {withCompare && !comparable ? <Sub data-note="scale">{t('performance.results.analytics.dist.compareScale')}</Sub> : null}
          {kind === 'bands' && data?.unbanded ? <Sub data-note="unbanded">{t('performance.results.analytics.dist.unbanded', { count: data.unbanded })}</Sub> : null}
          {table ? (
            <Table data-view="table">
              <thead>
                <tr>
                  <th>{t(`performance.results.analytics.dist.col.${kind}`)}</th>
                  <th>{t('performance.results.analytics.dist.col.count')}</th>
                  <th>{t('performance.results.analytics.dist.col.pct')}</th>
                  {showCompare ? <th>{compareName}</th> : null}
                  {onSelect ? <th aria-label={t('performance.results.analytics.dist.col.list')} /> : null}
                </tr>
              </thead>
              <tbody>
                {chartRows.map((r) => (
                  <tr key={r.key} data-row={r.key}>
                    <td>{r.name}</td>
                    <td>{formatNum(r.count, lang, 0)}</td>
                    <td>{r.pct === null || r.pct === undefined ? t('performance.results.analytics.noData') : formatPct(r.pct, lang)}</td>
                    {showCompare ? <td>{r.comparePct === null || r.comparePct === undefined ? '-' : `${formatNum(r.compareCount, lang, 0)} · ${formatPct(r.comparePct, lang)}`}</td> : null}
                    {onSelect ? (
                      <td>
                        {r.count ? (
                          <Clickable type="button" onClick={() => select(r)} data-action="drill">{t('performance.results.analytics.dist.openList')}</Clickable>
                        ) : null}
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : (
            <div style={{ width: '100%', height: 240 }} data-view="chart">
              <ResponsiveContainer>
                <BarChart data={chartRows} margin={{ top: 20, right: 8, left: 0, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="name" tick={{ fontSize: theme.metrics.font.size.xs }} interval={0} />
                  <YAxis unit="%" allowDecimals={false} tick={{ fontSize: theme.metrics.font.size.xs }} domain={[0, 100]} />
                  <Tooltip
                    formatter={(value, _name, item) => {
                      const isCompare = item?.dataKey === 'compareValue';
                      const count = isCompare ? item?.payload?.compareCount : item?.payload?.count;
                      return [`${formatNum(count, lang, 0)} · ${formatPct(value, lang)}`, isCompare ? compareName : t('performance.results.analytics.dist.current')];
                    }}
                  />
                  {showCompare ? <Legend wrapperStyle={{ fontSize: theme.metrics.font.size.xs }} /> : null}
                  <Bar
                    dataKey="value" name={t('performance.results.analytics.dist.current')} radius={[4, 4, 0, 0]}
                    fill={showCompare ? colors.mid : colors.none} cursor={onSelect ? 'pointer' : 'default'}
                    onClick={(d) => select(d?.payload || d)}
                  >
                    {showCompare ? null : chartRows.map((r) => <Cell key={r.key} fill={colors[r.band] || colors.none} />)}
                    <LabelList dataKey="label" position="top" style={{ fontSize: theme.metrics.font.size.xs, fill: theme.colors.text.primary }} />
                  </Bar>
                  {showCompare ? (
                    <Bar dataKey="compareValue" name={compareName} radius={[4, 4, 0, 0]} fill={colors.compare} />
                  ) : null}
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </>
      )}
    </PanelCard>
  );
}
